import { useMutation, useQuery, useQueryClient } from 'react-query'
import type { AxiosError } from 'axios'
import {
  fetchSavedRecipes,
  saveRecipe,
  unsaveRecipe,
} from '../api/calculatorApi'
import type { SavedRecipesResponse } from '../types'
import { CALCULATOR_KEYS } from './calculatorKeys'

/**
 * Рецепты, которые человек добавил себе для расчёта. Список личный и меняется
 * только этим экраном, поэтому держим его у себя и правим точечно.
 *
 * Карточки со снятым калькулятором из ответа выпадают, хотя сохранение
 * остаётся: «сохранял пять, вижу четыре» — штатная ситуация, а не потеря.
 */
export const useSavedRecipesQuery = (enabled = true) =>
  useQuery<SavedRecipesResponse, AxiosError>(
    CALCULATOR_KEYS.saved,
    fetchSavedRecipes,
    {
      enabled,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    }
  )

interface ToggleSavedInput {
  cardId: number
  /** Каким список должен стать после нажатия. */
  next: boolean
}

/**
 * Переключатель «считаю по этому рецепту».
 *
 * Обе ручки отвечают без тела, поэтому состояние держим сами и правим кеш
 * сразу, до ответа: иначе кнопка залипала бы на время запроса. При ошибке
 * возвращаем список, каким он был.
 *
 * Повторное сохранение и удаление отсутствующего бэк считает успехом, так что
 * расхождение с другим устройством ошибки не даст.
 */
export const useToggleSavedRecipeMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    void,
    AxiosError,
    ToggleSavedInput,
    SavedRecipesResponse | undefined
  >(({ cardId, next }) => (next ? saveRecipe(cardId) : unsaveRecipe(cardId)), {
    retry: false,
    onMutate: async ({ cardId, next }) => {
      await queryClient.cancelQueries(CALCULATOR_KEYS.saved)
      const previous = queryClient.getQueryData<SavedRecipesResponse>(
        CALCULATOR_KEYS.saved
      )
      if (previous) {
        const without = previous.card_ids.filter(id => id !== cardId)
        // Новые сверху — тем же порядком, что отдаёт бэк.
        queryClient.setQueryData<SavedRecipesResponse>(CALCULATOR_KEYS.saved, {
          card_ids: next ? [cardId, ...without] : without,
        })
      }
      return previous
    },
    onError: (error, _variables, previous) => {
      if (previous) {
        queryClient.setQueryData(CALCULATOR_KEYS.saved, previous)
      }
      // 404 значит, что калькулятор у карточки сняли — список рецептов,
      // из которого её предлагали, устарел.
      if (error.response?.status === 404) {
        void queryClient.invalidateQueries(CALCULATOR_KEYS.cards)
      }
    },
  })
}
